import { memo, useEffect, useMemo, useReducer, type ComponentType } from 'react'
import moment from 'moment'
import Sheet from 'components/utils/Sheet'
import FlightSearchWidgetStateProvider from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateProvider'
import { sortTripStopsByItineraryOrder } from 'tripPlanner/utils/itineraryStopOrder'
import { resolveSearchCalendarFocusDate } from 'tripPlanner/utils/searchCalendarFocus'
import flightSearchWidgetStateReducer, {
  createEmptyFlightSearchWidgetFlightItem,
  FlightSearchWidgetActions,
  initialState,
} from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import type { FlightSearchWidgetState, FlightSearchWidgetDispatchAction } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import ModalProvider from 'contexts/ModalProvider'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import { FlightsFareTypes, fareClassToUrlMap } from 'constants/flight'
import uuidV4 from 'lib/string/uuidV4Utils'
import ChatFlightSearchContent, { type FlightSearchDrawerComponentProps } from './ChatFlightSearchContent'
import ChatFlightErrorBoundary from './ChatFlightErrorBoundary'
import { normalizeFlightUIData } from './chatFlightTypes'
import type { FlightUIData } from './chatFlightTypes'
import type { TripItineraryData } from 'tripPlanner/api/types'

export type { FlightLeg, FlightOption, FlightUIData } from './chatFlightTypes'

interface Props {
  data: FlightUIData
  onSendMessage?: (message: string) => void
  isStreaming?: boolean
  itineraryData?: TripItineraryData
  FlightSearchDrawerComponent?: ComponentType<FlightSearchDrawerComponentProps>
}

interface ParsedSearchParams {
  fareType?: string
  fareClass?: string
  occupancies?: Array<{ adults: number }>
  itinerary?: Array<{ departureAirport: string; arrivalAirport: string; departureDate: string }>
}

function ChatFlightCarouselInner({
  data: rawData,
  onSendMessage,
  isStreaming,
  itineraryData,
  FlightSearchDrawerComponent,
}: Props) {
  const data = useMemo(() => normalizeFlightUIData(rawData), [rawData])
  const { fareTypes, fareCabins } = useFlightFareLabels()

  const parsedSearchParams = useMemo((): ParsedSearchParams | null => {
    if (!data.searchParams) return null
    try {
      return JSON.parse(data.searchParams) as ParsedSearchParams
    } catch {
      return null
    }
  }, [data.searchParams])

  const widgetInitialState = useMemo((): FlightSearchWidgetState => {
    const returnFareType = fareTypes.find(ft => ft.value === FlightsFareTypes.RETURN) ?? fareTypes[0]
    const state: FlightSearchWidgetState = {
      ...initialState,
      fareType: returnFareType,
      fareCabin: fareCabins[0],
    }

    if (parsedSearchParams) {
      const matchedFareType = fareTypes.find(ft => ft.value === parsedSearchParams.fareType)
      if (matchedFareType) state.fareType = matchedFareType

      const matchedCabin = fareCabins.find(fc => {
        const mapped = fareClassToUrlMap[fc.value]
        return mapped === parsedSearchParams.fareClass || fc.value === parsedSearchParams.fareClass
      })
      if (matchedCabin) state.fareCabin = matchedCabin

      if (parsedSearchParams.occupancies?.[0]) state.occupants = parsedSearchParams.occupancies[0]

      const itinerary = parsedSearchParams.itinerary
      if (itinerary?.[0]) {
        const firstOutbound = data.outboundOptions?.[0]?.leg
        if (state.fareType.value === FlightsFareTypes.MULTI_CITY) {
          state.flights = itinerary.map((leg, index) => createEmptyFlightSearchWidgetFlightItem({
            id: uuidV4(),
            departureAirport: {
              airportCode: leg.departureAirport,
              airportName: index === 0 ? firstOutbound?.departureAirportName ?? leg.departureAirport : leg.departureAirport,
            } as App.AirportLocation,
            arrivalAirport: {
              airportCode: leg.arrivalAirport,
              airportName: index === 0 ? firstOutbound?.arrivalAirportName ?? leg.arrivalAirport : leg.arrivalAirport,
            } as App.AirportLocation,
            checkinDate: moment(leg.departureDate),
          }))
        } else {
          state.flights = [{
            ...state.flights[0],
            id: uuidV4(),
            departureAirport: {
              airportCode: itinerary[0].departureAirport,
              airportName: firstOutbound?.departureAirportName ?? itinerary[0].departureAirport,
            } as App.AirportLocation,
            arrivalAirport: {
              airportCode: itinerary[0].arrivalAirport,
              airportName: firstOutbound?.arrivalAirportName ?? itinerary[0].arrivalAirport,
            } as App.AirportLocation,
            checkinDate: moment(itinerary[0].departureDate),
            checkoutDate: itinerary[1] ? moment(itinerary[1].departureDate) : undefined,
          }]
        }
        return state
      }
    }

    const firstOutbound = data.outboundOptions?.[0]?.leg
    if (firstOutbound) {
      const isReturn = data.tripType === 'Return'
      const matchedFareType = fareTypes.find(ft =>
        ft.value === (isReturn ? FlightsFareTypes.RETURN : FlightsFareTypes.ONE_WAY),
      )
      if (matchedFareType) state.fareType = matchedFareType

      const firstInbound = data.inboundOptions?.[0]?.leg
      if (data.passengers) state.occupants = { adults: data.passengers }
      state.flights = [{
        ...state.flights[0],
        id: uuidV4(),
        departureAirport: {
          airportCode: firstOutbound.departureAirport,
          airportName: firstOutbound.departureAirportName,
        } as App.AirportLocation,
        arrivalAirport: {
          airportCode: firstOutbound.arrivalAirport,
          airportName: firstOutbound.arrivalAirportName,
        } as App.AirportLocation,
        checkinDate: moment(firstOutbound.departureDate),
        checkoutDate: firstInbound ? moment(firstInbound.departureDate) : undefined,
      }]
    }

    return state
  }, [fareTypes, fareCabins, parsedSearchParams, data.outboundOptions, data.inboundOptions, data.passengers, data.tripType])

  /**
   * Which month the date calendars open on.
   *
   * The flight's own departure decides it when there is one; otherwise the
   * trip does. The resolver's last resort is the current month, so without the
   * trip's first stop a search for a trip planned months out opens on today
   * with the itinerary nowhere in sight.
   */
  const calendarFocusMonth = useMemo(() => {
    const firstDatedStop = sortTripStopsByItineraryOrder([...(itineraryData?.stops ?? [])])
      .find(stop => stop.stopType !== 'ORIGIN' && stop.startDate)

    /*
     * A placeholder leg carries no date, and `moment('')` still produces a
     * Date object — an invalid one — so validity has to be checked rather
     * than leaning on `??` to fall through to the trip.
     */
    const flightStart = widgetInitialState.flights[0]?.checkinDate

    return resolveSearchCalendarFocusDate({
      relevantConcreteStart: flightStart?.isValid()
        ? flightStart.toDate()
        : firstDatedStop?.startDate,
      indicativeSearchMonth: itineraryData?.indicativeSearchMonth,
    })
  }, [itineraryData, widgetInitialState])

  const [widgetState, widgetDispatch] = useReducer<FlightSearchWidgetState, [FlightSearchWidgetDispatchAction]>(
    flightSearchWidgetStateReducer,
    useMemo(() => ({ ...widgetInitialState, calendarFocusMonth }), [widgetInitialState, calendarFocusMonth]),
  )

  /*
   * `useReducer` reads its initial state once, so a month resolved from an
   * itinerary that arrives after mount would never reach the calendars. The
   * itinerary is fetched asynchronously, which is exactly that case.
   */
  useEffect(() => {
    if (!calendarFocusMonth) return
    widgetDispatch({
      type: FlightSearchWidgetActions.SET_CALENDAR_FOCUS_MONTH,
      calendarFocusMonth,
    })
  }, [calendarFocusMonth])

  if (!data.outboundOptions?.length && !data.segments?.length) return null

  return (
    <Sheet border="light-foreground" borderRadius="L">
      <FlightSearchWidgetStateProvider state={widgetState} dispatch={widgetDispatch}>
        <ModalProvider>
          <ChatFlightSearchContent
            data={data}
            onSendMessage={onSendMessage}
            isStreaming={isStreaming}
            itineraryData={itineraryData}
            FlightSearchDrawerComponent={FlightSearchDrawerComponent}
          />
        </ModalProvider>
      </FlightSearchWidgetStateProvider>
    </Sheet>
  )
}

function ChatFlightCarousel(props: Props) {
  return (
    <ChatFlightErrorBoundary>
      <ChatFlightCarouselInner {...props} />
    </ChatFlightErrorBoundary>
  )
}

export default memo(ChatFlightCarousel)
